import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wine, UtensilsCrossed, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Helmet } from 'react-helmet-async';
import { PageSEO } from '../components/SEO/PageSEO';
import useAnalytics from '../hooks/useAnalytics';
import LogoCarousel from '../components/LogoCarousel';
import { useEventForm } from '../context/EventFormContext';

const categories = [
  {
    number: '01',
    name: 'Drinks',
    icon: Wine,
    occasion: 'drinks',
    image: '/Inspo_Gallery/bar-bonobo-nice-entry-way-cocktail-bar.png',
  },
  {
    number: '02',
    name: 'Private Dining',
    icon: UtensilsCrossed,
    occasion: 'private-dining',
    image: '/Inspo_Gallery/Dirty-French-Private-Dining-scaled.jpeg',
  },
  {
    number: '03',
    name: 'Everything Else',
    icon: Sparkles,
    occasion: 'everything-else',
    image: '/Inspo_Gallery/nice-fancy-rooftop-no-people.avif',
  },
];

const timelineSteps = [
  {
    number: '01',
    title: 'Share Your Event Details',
    description: 'Tell us about your needs via phone call, email, or text—from intimate 25-person dinners to 250-guest full buyouts.',
  },
  {
    number: '02',
    title: 'Direct Venue Sourcing',
    description: 'We leverage our network of vetted venues to find the best private dining in NYC for you.',
  },
  {
    number: '03',
    title: 'Book in Minutes',
    description: 'The average event is booked in less than 15 minutes of your time. We handle the heavy lifting while you focus on hosting.',
  },
];

const landingPageFaqs = [
  {
    question: "How does LetsHost match me with the best private dining in NYC?",
    answer: "Stop spending hours searching Google or looking through the same 10 posts about private dining spots on The Infatuation. Tell us about your event details—guest count, date, budget, and style—and our concierge team will leverage our network of vetted venues to find your 2-3 perfect matches. We communicate however you prefer: phone call, email, or text."
  },
  {
    question: "What's the difference between LetsHost and sites like Tagvenue, PartyPlace, or PartySlate?",
    answer: "Unlike DIY directories where you're left clicking through generic listings and waiting for responses, LetsHost is a curated concierge service. We provide direct sourcing for high-quality events, giving you a shortcut to the city's best rooms. The average event is booked in less than 15 minutes of your time."
  },
  {
    question: "What types of events do you handle?",
    answer: "We specialize in high quality events including dinners (25-50 guests), cocktail parties, and full restaurant buyouts (50-250 guests) for both weekdays and weekends."
  },
  {
    question: "Is there a fee for your venue concierge service?",
    answer: "We charge a small booking fee for our matching and curation service. This allows our team to provide personalized, high-touch support via phone, email, and text to ensure you get guaranteed introductions to venues that actually fit your vision."
  }
];

const FAQItem = ({ faq, isOpen, toggle }) => (
  <div className="border-b border-stone-200 py-4">
    <button
      onClick={toggle}
      className="w-full flex justify-between items-center text-left focus:outline-none"
    >
      <span className="text-lg font-semibold text-gray-900 leading-tight pr-4" style={{ fontFamily: "'Playfair Display', serif" }}>
        {faq.question}
      </span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-stone-400 flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-stone-400 flex-shrink-0" />
      )}
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <p className="mt-3 text-stone-600 leading-relaxed text-sm">
            {faq.answer}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const homePageSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "LetsHost NYC",
    "url": "https://letshost.nyc",
    "logo": "https://letshost.nyc/logo.png",
    "description": "NYC's premier venue concierge service matching you with amazing event spaces.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "New York",
      "addressRegion": "NY",
      "postalCode": "10001",
      "addressCountry": "US"
    },
    "offers": {
      "@type": "Offer",
      "description": "NYC venue matchmaking service for events",
      "areaServed": {
        "@type": "City",
        "name": "New York City"
      }
    }
  };

  const { trackEvent, trackButtonClick } = useAnalytics();
  const { resetForNewSubmission } = useEventForm();

  useEffect(() => {
    trackEvent('Landing Page View', {
      referrer: document.referrer || 'direct',
      timestamp: new Date().toISOString(),
    });
  }, [trackEvent]);

  const [activeCard, setActiveCard] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setActiveCard((prev) => (prev + 1) % categories.length);
    }, 4000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const cardRefs = useRef([]);

  const scrollArrowToCard = (index) => {
    const card = cardRefs.current[index];
    if (card) {
      const cardRect = card.getBoundingClientRect();
      const containerRect = card.closest('.category-grid')?.getBoundingClientRect();
      if (containerRect) {
        const offsetX = cardRect.left - containerRect.left + cardRect.width / 2 - 10;
        return offsetX;
      }
      return 0;
    }
    return 0;
  };

  const handleCategoryClick = (category) => {
    resetForNewSubmission(category.occasion);
    
    trackEvent('Category Card Click', {
      category: category.name,
      occasion: category.occasion,
    });
    trackButtonClick('category_card', { category: category.name });
    navigate('/details');
  };

  return (
    <div className="min-h-screen flex flex-col bg-white relative">
      <PageSEO
        title="Best Private Dining in NYC | LetsHost Venue Concierge"
        description="Your shortcut to the city's best event spaces. We provide direct sourcing for high-end events and vetted private dining in NYC for groups of 25-250 guests."
        path="/"
        ogImage="/letshost-share-preview.png"
        preloadLcpImage="/Landing_Page_Collage/webp/ArloSoho_rooftop.webp"
        faqSchema={landingPageFaqs}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(homePageSchema)}
        </script>
      </Helmet>

      {/* ───────── HERO + CATEGORY CARDS ───────── */}
      <section className="relative min-h-[75vh] sm:min-h-[74vh] flex flex-col">
        <picture className="absolute inset-0">
          <source srcSet="/Landing_Page_Collage/webp/ArloSoho_rooftop.webp" type="image/webp" />
          <img
            src="/Landing_Page_Collage/ArloSoho_rooftop.jpg"
            alt="Arlo SoHo rooftop venue with stunning NYC skyline views"
            className="w-full h-full object-cover"
            loading="eager"
          />
        </picture>
        <div className="absolute inset-0 bg-black/45" />

        <div className="relative z-10">
          <Navbar transparent />
        </div>

        <div className="relative z-10 flex-1 flex flex-col justify-center px-5 sm:px-10 lg:px-16 pb-6 sm:pb-10 pt-2 sm:pt-24 lg:pt-0">
          <motion.h1
            className="text-[3rem] sm:text-[3.8rem] md:text-[4.4rem] lg:text-[4.5rem] xl:text-[5.5rem] font-bold text-white leading-[1.05] max-w-4xl"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.02em' }}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            The City's Best Venues,<br className="hidden sm:block" /> Hand-Picked for You.
          </motion.h1>

          <motion.p
            className="mt-6 sm:mt-10 mb-8 sm:mb-10 text-xl sm:text-2xl tracking-wide text-white font-medium font-sans"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            Select an occasion to get started:
          </motion.p>
          {/* Category cards inside hero */}
          <motion.div
            className="relative pl-9 sm:pl-0 max-w-[340px] sm:max-w-4xl w-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {/* Desktop arrow — moves horizontally above cards */}
            <motion.div
              className="absolute top-[-40px] left-0 hidden sm:block"
              animate={{ left: scrollArrowToCard(activeCard) }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-white/70"
              >
                <path
                  d="M10 3V17M10 17L4 11M10 17L16 11"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.div>

            <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6 w-full category-grid">
              {/* Mobile arrow — moves vertically, points right */}
              <motion.div
                className="absolute left-[-28px] top-0 sm:hidden"
                animate={{
                  top: cardRefs.current[activeCard]
                    ? cardRefs.current[activeCard].offsetTop +
                      cardRefs.current[activeCard].offsetHeight / 2 -
                      12
                    : activeCard * 90,
                }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-white/70"
                >
                  <path
                    d="M3 10H17M17 10L11 4M17 10L11 16"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </motion.div>

              {categories.map((cat, idx) => {
                const Icon = cat.icon;
                const isActive = activeCard === idx;
                return (
                  <motion.button
                    key={cat.number}
                    ref={(el) => (cardRefs.current[idx] = el)}
                    onClick={() => {
                      setActiveCard(idx);
                      handleCategoryClick(cat);
                    }}
                    onMouseEnter={() => setActiveCard(idx)}
                    animate={isActive ? { y: [0, 0, -4], transition: { duration: 0.3 } } : { y: 0 }}
                    whileTap={{ scale: 0.97 }}
                    className={`bg-white rounded-xl text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/60 group shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden border flex items-center sm:flex-col sm:items-stretch ${
                      isActive ? 'border-white sm:ring-2 sm:ring-white/30' : 'border-white/90'
                    }`}
                  >
                    <div className="p-1.5 sm:p-2.5 w-40 sm:w-full shrink-0">
                      <div className="relative w-full overflow-hidden rounded-lg" style={{ aspectRatio: '16 / 9' }}>
                        <img
                          src={cat.image}
                          alt={cat.name}
                          className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                    </div>
                    <div className="px-3 sm:px-3.5 py-2.5 sm:pb-3.5 sm:pt-1 flex-1 flex items-center sm:items-end justify-between w-full">
                      <span
                        className="text-base sm:text-lg md:text-xl font-semibold text-gray-900"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {cat.name}
                      </span>
                      <Icon className="w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8 text-stone-400 stroke-[1.2]" />
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ───────── LOGO CAROUSEL ───────── */}
      <section className="w-full py-8 sm:py-12" style={{ backgroundColor: '#F5F0EB' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <LogoCarousel />
        </div>
      </section>

      {/* ───────── TIMELINE / HOW IT WORKS ───────── */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-14"
            style={{ fontFamily: "'Playfair Display', serif" }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Timeline
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative">
            <div className="hidden md:block absolute top-14 left-[10%] right-[10%] h-px bg-stone-200/60" />

            {timelineSteps.map((step, idx) => (
              <motion.div
                key={step.number}
                className="flex flex-col relative"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: idx * 0.15 }}
              >
                <span
                  className="text-7xl sm:text-8xl font-bold leading-none mb-3"
                  style={{ fontFamily: "'Playfair Display', serif", color: '#E8DDD3' }}
                >
                  {step.number}
                </span>
                <h3
                  className="text-lg font-semibold text-gray-900 mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {step.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed max-w-xs">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── FAQ SECTION ───────── */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#FAFAF8]">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12 text-center"
          >
            <h2
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Common Questions
            </h2>
            <p className="text-stone-500">
              Everything you need to know about booking with LetsHost.
            </p>
          </motion.div>

          <div className="space-y-2">
            {landingPageFaqs.map((faq, idx) => (
              <FAQItem 
                key={idx} 
                faq={faq} 
                isOpen={openFaqIndex === idx} 
                toggle={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)} 
              />
            ))}
          </div>
        </div>
      </section>

      {/* ───────── FOOTER ───────── */}
      <Footer />
    </div>
  );
}
